import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import {
    emphasize,
    ExcelCell,
    ExcelRow,
    ExcelSheet,
    integer,
    money,
    ROW_KIND
} from '../../statistics/StatisticsExcel.Functions';
import {
    ConceptValuationOptionsEnum,
    conceptsForTotals,
    getSettlementLabel,
    getStagesForBranch,
    getValuationLabel,
    isGroupedInAnyStage,
    isOptionalConcept,
    isSharedStage,
    resolveAmountCurrency
} from './ConceptGroupedView.Functions';
import {
    allExecutions,
    ExecutionKey,
    getConceptTotalForStage,
    getDisplayedCellTotal,
    getExecutionTotalFor,
    getQuantityForStage,
    getRelevantExecutionFor,
    getStageScheduleFor,
    isRemoteStage
} from './ConceptGroupedView.Pricing';

/**
 * Exportación de la grilla de conceptos agrupados.
 *
 * La hoja de conceptos por etapa se arma igual que la pantalla: conceptos en
 * filas, visitas en columnas —cada una abierta en sus tres modalidades— y en
 * negrita exactamente lo que en la página va en negrita: lo que suma al total
 * general. Hay una hoja por solapa de rama, así el archivo trae todas las ramas y
 * no sólo la que estaba abierta.
 *
 * Una celda de Excel no puede tener una parte en negrita y otra no, así que la
 * fila de totales de la pantalla se abre en dos: el total de cada columna y, abajo,
 * la parte que suma al total general.
 */

const SI = 'Sí';
const NO = 'No';

const FIXED_HEADERS = ['Concepto', 'Liquidación', 'Valuación', 'Opcional', 'Moneda', 'Precio base'];
const TRAILING_HEADERS = ['Cantidad', 'Total a liquidar'];

/**
 * Las grillas a exportar. Con ramas, una por rama: las visitas sin rama ya están
 * dentro de cada una, así que una grilla aparte de "sin rama" las repetiría. Sin
 * ramas, una sola grilla con todas las etapas, como la pantalla.
 */
const branchScopes = (
    stages: ContractStage[],
    branches: ContractBranch[]
): { id: number | undefined; name: string }[] => {
    const scopes: { id: number | undefined; name: string }[] = [];

    const hasBranches = (branches || []).length > 0 || (stages || []).some((stage) => !isSharedStage(stage));
    if (!hasBranches) return [{ id: undefined, name: 'Todas las etapas' }];

    (branches || []).forEach((branch) => {
        scopes.push({ id: branch.id, name: (branch.branchName || '').trim() || `Rama #${branch.id}` });
    });

    // Una etapa puede apuntar a una rama que no está en la lista del addendum.
    // `branchId` llega como number, null o undefined: se normaliza a uno solo.
    const known = new Set<number | undefined>(scopes.map((scope) => scope.id));
    (stages || []).forEach((stage) => {
        const branchId = stage.branchId ?? undefined;
        if (branchId === undefined || known.has(branchId)) return;
        known.add(branchId);
        scopes.push({ id: branchId, name: `Rama #${branchId}` });
    });

    return scopes;
};

/** Clave de columna por visita y modalidad. El título que se ve va aparte. */
const columnKey = (stage: ContractStage, execution: ExecutionKey): string => `visita-${stage.id}-${execution}`;

const currencyOfCell = (concept: ContractConcept, stage: ContractStage, contractCurrency?: string): string =>
    resolveAmountCurrency(concept.conceptCurrency, getStageScheduleFor(concept, stage.id)?.currency, contractCurrency) || '';

/** El concepto liquida en esa etapa: tiene fila de programación o se agrupa en la visita. */
const settlesIn = (concept: ContractConcept, stage: ContractStage): boolean =>
    !!getStageScheduleFor(concept, stage.id) || isGroupedInAnyStage(concept);

export interface MatrixSheetInput {
    name: string;
    /** Rama de la solapa; undefined es la solapa de las visitas sin rama. */
    branchId: number | undefined;
    concepts: ContractConcept[];
    stages: ContractStage[];
    currency?: string;
    includeOptional: boolean;
}

/** La grilla de una solapa: conceptos en filas, visitas por modalidad en columnas. */
export const matrixSheet = (input: MatrixSheetInput): ExcelSheet => {
    const stages = getStagesForBranch(input.stages, input.branchId);
    const concepts = input.concepts || [];

    const stageHeaders: string[] = [];
    const headerLabels: Record<string, string> = {};
    const headerGroups: { label: string; span: number }[] = [{ label: '', span: FIXED_HEADERS.length }];

    stages.forEach((stage) => {
        allExecutions.forEach((execution) => {
            const key = columnKey(stage, execution.key);
            stageHeaders.push(key);
            headerLabels[key] = execution.short;
        });

        const shared = input.branchId !== undefined && isSharedStage(stage) ? ' · Todas las ramas' : '';
        headerGroups.push({
            label: `${stage.stageName || `Visita #${stage.id}`} · ${isRemoteStage(stage) ? 'Remota' : 'Presencial'}${shared}`,
            span: allExecutions.length
        });
    });

    headerGroups.push({ label: '', span: TRAILING_HEADERS.length });

    // ── Una fila por concepto ──
    const rows: ExcelRow[] = concepts.map((concept) => {
        const counts = input.includeOptional || !isOptionalConcept(concept);
        const row: ExcelRow = {
            Concepto: concept.concept?.conceptName || `Concepto #${concept.id}`,
            Liquidación: getSettlementLabel(concept.settlementSetting),
            Valuación: getValuationLabel(concept.valuationType),
            Opcional: isOptionalConcept(concept) ? (counts ? SI : 'Sí (no suma)') : NO,
            Moneda: resolveAmountCurrency(
                concept.conceptCurrency,
                stages.map((stage) => getStageScheduleFor(concept, stage.id)?.currency).find(Boolean),
                input.currency
            ) || '',
            'Precio base': concept.valuationType === ConceptValuationOptionsEnum.ValueByStage
                ? ''
                : money(concept.priceValue ?? 0)
        };

        let quantity = 0;
        let total = 0;

        stages.forEach((stage) => {
            const schedule = getStageScheduleFor(concept, stage.id);
            const settles = settlesIn(concept, stage);
            const relevant = getRelevantExecutionFor(concept, schedule, stage);

            allExecutions.forEach((execution) => {
                const shown = settles ? getDisplayedCellTotal(concept, schedule, execution.key) : undefined;
                let cell: ExcelCell = '';
                if (shown !== undefined) {
                    // En negrita, lo mismo que en la página: la modalidad que liquida,
                    // en una fila que suma.
                    cell = counts && execution.key === relevant ? emphasize(money(shown)) : money(shown);
                }
                row[columnKey(stage, execution.key)] = cell;
            });

            if (!settles) return;
            quantity += getQuantityForStage(schedule);
            total += getConceptTotalForStage(concept, schedule, stage);
        });

        row.Cantidad = quantity || '';
        row['Total a liquidar'] = counts ? emphasize(money(total)) : money(total);
        return row;
    });

    // ── Totales, separados por moneda: nunca se suman monedas distintas ──
    const columnSums = new Map<string, Map<string, number>>();
    const countedSums = new Map<string, Map<string, number>>();
    const quantities = new Map<string, number>();
    const grandTotals = new Map<string, number>();

    const add = (target: Map<string, Map<string, number>>, currency: string, key: string, amount: number) => {
        if (!target.has(currency)) target.set(currency, new Map());
        const bucket = target.get(currency)!;
        bucket.set(key, (bucket.get(key) || 0) + amount);
    };

    conceptsForTotals(concepts, input.includeOptional).forEach((concept) => {
        stages.forEach((stage) => {
            if (!settlesIn(concept, stage)) return;

            const schedule = getStageScheduleFor(concept, stage.id);
            const currency = currencyOfCell(concept, stage, input.currency);
            const relevant = getRelevantExecutionFor(concept, schedule, stage);
            const relevantTotal = getExecutionTotalFor(concept, schedule, relevant);

            quantities.set(currency, (quantities.get(currency) || 0) + getQuantityForStage(schedule));
            grandTotals.set(currency, (grandTotals.get(currency) || 0) + relevantTotal);
            add(countedSums, currency, columnKey(stage, relevant), relevantTotal);

            allExecutions.forEach((execution) => {
                const shown = getDisplayedCellTotal(concept, schedule, execution.key);
                if (shown !== undefined) add(columnSums, currency, columnKey(stage, execution.key), shown);
            });
        });
    });

    const currencies = Array.from(new Set([...Array.from(columnSums.keys()), ...Array.from(grandTotals.keys())]));
    const suffix = (currency: string) => (currencies.length > 1 && currency ? ` (${currency})` : '');

    currencies.forEach((currency) => {
        const columnRow: ExcelRow = { [ROW_KIND]: 'subtotal', Concepto: `Total de la columna${suffix(currency)}`, Moneda: currency };
        const countedRow: ExcelRow = { [ROW_KIND]: 'total', Concepto: `Suma al total general${suffix(currency)}`, Moneda: currency };

        columnSums.get(currency)?.forEach((amount, key) => {
            columnRow[key] = money(amount);
        });
        countedSums.get(currency)?.forEach((amount, key) => {
            countedRow[key] = emphasize(money(amount));
        });

        columnRow.Cantidad = integer(quantities.get(currency) || 0);
        countedRow['Total a liquidar'] = emphasize(money(grandTotals.get(currency) || 0));

        rows.push({}, columnRow, countedRow);
    });

    return {
        name: `Por etapa - ${input.name}`,
        title: `Conceptos por etapa · ${input.name}`,
        description: `Conceptos en filas y visitas en columnas, en la rama ${input.name}.`,
        note: [
            'En negrita, los valores que suman al total general: la modalidad que liquida cada visita.',
            input.includeOptional ? '' : 'Los conceptos opcionales no suman.'
        ].filter(Boolean).join(' '),
        headers: [...FIXED_HEADERS, ...stageHeaders, ...TRAILING_HEADERS],
        headerLabels,
        headerGroups,
        autofilter: false,
        rows
    };
};

/** Las dos tablas que no son por etapa: mismas columnas que en pantalla. */
export const genericRows = (concepts: ContractConcept[], contractCurrency?: string): ExcelRow[] =>
    (concepts || []).map((concept) => ({
        Concepto: concept.concept?.conceptName || `Concepto #${concept.id}`,
        Referencia: (concept.concept?.conceptReferenceId as any) || '',
        Liquidación: getSettlementLabel(concept.settlementSetting),
        Valuación: getValuationLabel(concept.valuationType),
        Moneda: resolveAmountCurrency(concept.conceptCurrency, contractCurrency) || '',
        Precio: money(concept.priceValue ?? 0),
        Presencial: concept.applyToInFaceExecution ? SI : NO,
        Remoto: concept.applyToRemoteExecution ? SI : NO,
        Telefónico: concept.applyToPhoneExecution ? SI : NO
    }));

export interface ConceptGridExport {
    byStage: ContractConcept[];
    inAnyStage: ContractConcept[];
    others: ContractConcept[];
    stages: ContractStage[];
    branches: ContractBranch[];
    currency?: string;
    /** Lo que eligió el usuario en la grilla: define qué suma y qué va en negrita. */
    includeOptional: boolean;
}

/** El libro completo: una hoja de matriz por rama y las otras dos tablas. */
export const conceptGridSheets = (input: ConceptGridExport): ExcelSheet[] => [
    ...branchScopes(input.stages, input.branches).map((scope) =>
        matrixSheet({
            name: scope.name,
            branchId: scope.id,
            concepts: input.byStage,
            stages: input.stages,
            currency: input.currency,
            includeOptional: input.includeOptional
        })
    ),
    {
        name: 'En cualquier etapa',
        description: 'Conceptos que se liquidan en cualquier etapa.',
        rows: genericRows(input.inAnyStage, input.currency)
    },
    {
        name: 'Otros conceptos',
        description: 'Conceptos que no se liquidan por etapa.',
        rows: genericRows(input.others, input.currency)
    }
];
